'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { CountUp } from '@/components/ui/count-up'
import { PageSpeedGauge } from '@/components/marketing/pagespeed-gauge'
import { IdukkiTile } from '@/components/marketing/idukki-tile'
import { EASE, SPRING } from '@/lib/motion'

// Persona set-piece for /solutions/[role]. One hero visual per role, each
// animating the thing that role actually stares at: the CMO's attribution
// ledger, the CRO's significance bar, the CTO's PageSpeed dials, the Head
// of Ecommerce's CR/AOV read-out, the agency's brand switcher. Every
// number shown already appears in that role's page copy (metrics, jobs or
// proof rows); the PageSpeed scores are the repo's real, dated Lighthouse
// measurement of the live widget (see pagespeed-proof.tsx).
// Plays once on scroll-in; transform/opacity only; reduced motion = final state.

type Ctx = { done: boolean; reduce: boolean; d: (n: number) => number }

export function SolutionPersonaHero({ role, roleLabel }: { role: string; roleLabel: string }) {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduce = useReducedMotion() ?? false
  const ctx: Ctx = { done: inView, reduce, d: (n) => (reduce ? 0 : n) }

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative">
        <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/10 via-transparent to-emerald-200/20 blur-2xl" />
        <div className="overflow-hidden rounded-3xl border bg-card shadow-2xl shadow-primary/5">
          <div className="flex items-center justify-between border-b bg-secondary/40 px-5 py-3">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inset-0 rounded-full bg-emerald-500 motion-safe:animate-ping opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-medium uppercase tracking-wider text-foreground">Sample workspace</span>
            </div>
            <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {roleLabel.replace(/^For (the )?/i, '')}
            </span>
          </div>
          <div className="p-5">
            {role === 'cmo' && <CmoLedger {...ctx} />}
            {role === 'cro' && <CroSignificance {...ctx} />}
            {role === 'cto' && <CtoGauges {...ctx} />}
            {role === 'ecommerce-leader' && <EcomReadout {...ctx} />}
            {role === 'agency' && <AgencySwitcher {...ctx} />}
          </div>
        </div>
      </div>
    </LazyMotion>
  )
}

/* ─── Shared bits ──────────────────────────────────────────────────── */

function Row({ done, reduce, delay, children, className }: {
  done: boolean
  reduce: boolean
  delay: number
  children: React.ReactNode
  className?: string
}) {
  return (
    <m.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={done ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.45, ease: EASE, delay: reduce ? 0 : delay }}
    >
      {children}
    </m.div>
  )
}

function Chip({ done, reduce, delay, children, tone = 'bg-secondary text-foreground' }: {
  done: boolean
  reduce: boolean
  delay: number
  children: React.ReactNode
  tone?: string
}) {
  return (
    <m.span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}
      initial={reduce ? false : { opacity: 0, scale: 0.85 }}
      animate={done ? { opacity: 1, scale: 1 } : undefined}
      transition={reduce ? { duration: 0 } : { ...SPRING, delay }}
    >
      {children}
    </m.span>
  )
}

function PanelLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">{children}</p>
}

/* ─── CMO: attribution ledger ──────────────────────────────────────── */

// Real, named, sourced customers only (lib/copy/case-studies.ts). No
// composite/invented brands or numbers, per the repo's content-integrity
// rule; the footer count-up is Theater's real, A/B-tested lift.
const CMO_ROWS = [
  { layout: 'Shoppable galleries · PDP + Home', brand: 'Leroy Merlin', stat: '$651K attributed' },
  { layout: 'Shoppable galleries · PDP',        brand: 'Theater',      stat: '+20% CR' },
]

function CmoLedger({ done, reduce }: Ctx) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <PanelLabel>Revenue attribution · per layout</PanelLabel>
        <span className="text-[10px] font-mono text-muted-foreground">GA4 · Meta CAPI</span>
      </div>
      <ul className="mt-3 space-y-2">
        {CMO_ROWS.map((r, i) => (
          <Row key={r.layout} done={done} reduce={reduce} delay={0.15 + i * 0.14} className="flex items-center justify-between gap-3 rounded-xl border bg-background px-3 py-2.5">
            <div className="min-w-0 leading-tight">
              <p className="truncate text-[11px] font-semibold text-foreground">{r.layout}</p>
              <p className="truncate text-[10px] text-muted-foreground">{r.brand}</p>
            </div>
            <Chip done={done} reduce={reduce} delay={0.3 + i * 0.14} tone="bg-emerald-500/10 text-emerald-700">
              {r.stat}
            </Chip>
          </Row>
        ))}
      </ul>
      <Row done={done} reduce={reduce} delay={0.62} className="mt-4 flex items-end justify-between rounded-xl border bg-secondary/40 px-4 py-3">
        <div>
          <p className="font-display text-3xl font-semibold tabular-nums text-foreground">
            <CountUp value={20} prefix="+" suffix="%" />
          </p>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">PDP conversion lift, Theater A/B test</p>
        </div>
        <span className="text-[10px] text-muted-foreground">audited · rights trail attached</span>
      </Row>
    </div>
  )
}

/* ─── CRO: significance bar ────────────────────────────────────────── */

function CroSignificance({ done, reduce, d }: Ctx) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <PanelLabel>Experiment · UGC gallery vs studio-only</PanelLabel>
        <span className="text-[10px] font-mono text-muted-foreground">50/50 A/B split</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Chip done={done} reduce={reduce} delay={d(0.1)} tone="bg-slate-500/10 text-slate-600">Control · studio-only</Chip>
        <Chip done={done} reduce={reduce} delay={d(0.2)} tone="bg-primary/10 text-primary">Variant · UGC gallery</Chip>
        <Chip done={done} reduce={reduce} delay={d(0.3)} tone="bg-secondary text-muted-foreground">30K+ visitors / arm</Chip>
      </div>
      <div className="mt-5 rounded-xl border bg-background px-4 py-4">
        <div className="flex items-baseline justify-between">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Significance</p>
          <p className="font-display text-2xl font-semibold tabular-nums text-foreground">
            <CountUp value={95} suffix="%" duration={1.4} />
          </p>
        </div>
        <div className="relative mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <m.div
            className="absolute inset-y-0 left-0 w-full rounded-full bg-primary"
            style={{ originX: 0 }}
            initial={reduce ? { scaleX: 0.95 } : { scaleX: 0 }}
            animate={done ? { scaleX: 0.95 } : undefined}
            transition={{ duration: 1.4, ease: EASE, delay: d(0.2) }}
          />
          {/* The promotion bar sits at 95%: nothing ships below it. */}
          <span aria-hidden className="absolute inset-y-0 left-[95%] w-px bg-foreground/50" />
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground">Variants promote only after they clear the bar.</p>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Chip done={done} reduce={reduce} delay={d(1.5)} tone="bg-emerald-500/10 text-emerald-700">
          Promote · +20% conversion (Theater)
        </Chip>
        <span className="text-[10px] font-mono text-muted-foreground">37 KB runtime · CWV holds</span>
      </div>
    </div>
  )
}

/* ─── CTO: PageSpeed dials + runtime weight ────────────────────────── */

// The dial values are the repo's real, dated PageSpeed measurement of the
// live widget (source of truth: components/marketing/pagespeed-proof.tsx).
const CTO_SCORES = [
  { label: 'Performance', value: 99 },
  { label: 'Accessibility', value: 100 },
  { label: 'Best practices', value: 100 },
]

function CtoGauges({ done, reduce, d }: Ctx) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <PanelLabel>PageSpeed · widget.idukki.io · Desktop</PanelLabel>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
          <IdukkiTile size="xs" /> production
        </span>
      </div>
      <ul className="mt-3 grid grid-cols-3 gap-2">
        {CTO_SCORES.map((s, i) => (
          <Row key={s.label} done={done} reduce={reduce} delay={0.1 + i * 0.1} className="flex flex-col items-center gap-1.5 rounded-xl border bg-background px-2 py-3">
            <PageSpeedGauge value={s.value} />
            <span className="text-[10px] font-medium text-muted-foreground">{s.label}</span>
          </Row>
        ))}
      </ul>
      <Row done={done} reduce={reduce} delay={0.45} className="mt-3 flex items-end justify-between rounded-xl border bg-secondary/40 px-4 py-3">
        <div>
          <p className="font-display text-3xl font-semibold tabular-nums text-foreground">
            <CountUp value={37} suffix=" KB" />
          </p>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Widget runtime, gzipped</p>
        </div>
        <div className="flex flex-wrap justify-end gap-1.5">
          <Chip done={done} reduce={reduce} delay={d(0.7)} tone="bg-emerald-500/10 text-emerald-700">CLS 0.001</Chip>
          <Chip done={done} reduce={reduce} delay={d(0.8)} tone="bg-emerald-500/10 text-emerald-700">INP &lt; 200ms</Chip>
          <Chip done={done} reduce={reduce} delay={d(0.9)} tone="bg-secondary text-muted-foreground">99.95% uptime</Chip>
        </div>
      </Row>
    </div>
  )
}

/* ─── Head of Ecommerce: CR / AOV read-out ─────────────────────────── */

// Both real, from Theater's A/B test (lib/copy/case-studies.ts); bars
// encode the same two numbers on a shared 0-30% axis.
const ECOM_STATS = [
  { label: 'PDP conversion lift', value: 20, fill: 20 / 30 },
  { label: 'Add-to-cart lift',    value: 25, fill: 25 / 30 },
]

function EcomReadout({ done, reduce, d }: Ctx) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <PanelLabel>Storefront read-out · Theater, PDP A/B test</PanelLabel>
        <span className="text-[10px] font-mono text-muted-foreground">5M+ impressions · 4 mo</span>
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-2">
        {ECOM_STATS.map((s, i) => (
          <Row key={s.label} done={done} reduce={reduce} delay={0.12 + i * 0.14} className="rounded-xl border bg-background px-4 py-3">
            <p className="font-display text-2xl font-semibold tabular-nums text-foreground">
              <CountUp value={s.value} prefix="+" suffix="%" />
            </p>
            <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-secondary">
              <m.span
                className="block h-full rounded-full bg-primary"
                style={{ originX: 0, width: `${s.fill * 100}%` }}
                initial={reduce ? false : { scaleX: 0 }}
                animate={done ? { scaleX: 1 } : undefined}
                transition={{ duration: 0.9, ease: EASE, delay: d(0.3 + i * 0.14) }}
              />
            </span>
          </Row>
        ))}
      </ul>
      <Row done={done} reduce={reduce} delay={0.5} className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-secondary/40 px-4 py-3">
        <Chip done={done} reduce={reduce} delay={d(0.65)} tone="bg-primary/10 text-primary">First widget in 4 minutes</Chip>
        <Chip done={done} reduce={reduce} delay={d(0.78)} tone="bg-secondary text-muted-foreground">Migration in 1 day</Chip>
      </Row>
    </div>
  )
}

/* ─── Agency: multi-brand switcher ─────────────────────────────────── */

// Illustrative vertical chips (not a specific customer's roster); "up to
// 50 brands per workspace" is the real, published plan limit (see
// /agencies and features-registry.ts), not a customer-specific figure.
const AGENCY_BRANDS = ['Fashion', 'Beauty', 'Home', 'Outdoor', 'Jewellery', 'Food', 'Fitness', 'Hotel']

function AgencySwitcher({ done, reduce, d }: Ctx) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <PanelLabel>Brand switcher · one login</PanelLabel>
        <span className="text-[10px] font-mono text-muted-foreground">role-scoped access</span>
      </div>
      <ul className="mt-3 flex flex-wrap gap-1.5">
        {AGENCY_BRANDS.map((b, i) => (
          <m.li
            key={b}
            className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-[11px] font-medium text-foreground"
            initial={reduce ? false : { opacity: 0, y: 6, scale: 0.94 }}
            animate={done ? { opacity: 1, y: 0, scale: 1 } : undefined}
            transition={reduce ? { duration: 0 } : { ...SPRING, delay: 0.1 + i * 0.09 }}
          >
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-lime ring-1 ring-foreground/10" />
            {b}
          </m.li>
        ))}
        <m.li
          className="inline-flex items-center rounded-full border border-dashed bg-secondary/40 px-2.5 py-1 text-[11px] text-muted-foreground"
          initial={reduce ? false : { opacity: 0, y: 6 }}
          animate={done ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.35, ease: EASE, delay: d(0.1 + AGENCY_BRANDS.length * 0.09) }}
        >
          + 6 more
        </m.li>
      </ul>
      <Row done={done} reduce={reduce} delay={0.95} className="mt-4 flex items-end justify-between rounded-xl border bg-secondary/40 px-4 py-3">
        <div>
          <p className="font-display text-3xl font-semibold tabular-nums text-foreground">
            <CountUp value={50} />
          </p>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Brands per workspace, max</p>
        </div>
        <div className="text-right text-[10px] leading-relaxed text-muted-foreground">
          <p>One login, role-scoped per client</p>
          <p>Weekly report → each client&rsquo;s Slack</p>
        </div>
      </Row>
    </div>
  )
}
